import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import Select from 'react-select'
import { useAuth } from '@astro/auth'
import { api } from '@astro/api'
import { useToast } from '../../context/ToastContext.jsx'
import DeliveryEstimate from '../common/DeliveryEstimate.jsx'

/** Common keys upstream might use for the address list, besides `data`. */
const LIST_KEYS = ['data', 'addresses', 'address', 'items', 'list', 'results', 'result', 'records']

/**
 * Unwrap a saved-address list out of whatever envelope the upstream API used —
 * `{ status, data }`, a Laravel paginator, or a differently-named key
 * (`address`, `addresses`, …). Upstream shape here is loosely documented, so
 * this walks a few likely keys before falling back to the first array found.
 */
export function toList(res, depth = 0) {
  if (Array.isArray(res)) return res
  if (!res || typeof res !== 'object' || depth > 3) return []

  for (const key of LIST_KEYS) {
    if (key in res) {
      const found = toList(res[key], depth + 1)
      if (found.length) return found
    }
  }
  // Fallback: first array-valued property anywhere on this object.
  for (const value of Object.values(res)) {
    if (Array.isArray(value)) return value
  }
  return []
}

const displayName = (a) =>
  a.name || [a.shipping_first_name, a.shipping_last_name].filter(Boolean).join(' ') || 'Saved address'

/** `is_default` comes back as 1/0, true/false, or "1"/"true" depending on the upstream call — normalize all of them. */
export const isDefault = (a) => {
  const v = a.is_default
  return typeof v === 'string' ? ['1', 'true', 'yes'].includes(v.toLowerCase()) : Boolean(Number(v)) || v === true
}

/** Map a saved address record into the modal's form shape. Records use
 * `shipping_*` keys (nested {id,name} for state/city) with flat fallbacks. */
function toForm(a) {
  const parts = String(displayName(a)).split(/\s+/)
  return {
    firstName: a.first_name || parts[0] || '',
    lastName: a.last_name || parts.slice(1).join(' '),
    mobile: String(a.mobile || a.shipping_phone_number || '').replace(/^\+?91[-\s]?/, ''),
    pincode: a.pincode || a.shipping_zip_code || '',
    address: a.address || a.billing_address || a.shipping_address || '',
    state: a.shipping_state?.name || a.state || '',
    stateId: a.shipping_state?.id ?? a.state_id ?? null,
    city: a.shipping_city?.name || a.city || '',
    cityId: a.shipping_city?.id ?? a.city_id ?? null,
    landmark: a.landmark || '',
    type: String(a.address_type || a.type || 'home').toLowerCase(),
  }
}

const EMPTY_FORM = {
  firstName: '', lastName: '', mobile: '', pincode: '', address: '',
  state: '', stateId: null, city: '', cityId: null, landmark: '', type: 'home',
}

/**
 * Accordion address book for the cart page. Expanding it lists the account's
 * saved addresses (GET /address-get) with Deliver-Here / Edit / Remove actions
 * and an add button; add/edit run through a popup form.
 */
export default function AddressBook({ onSelect }) {
  const { status, user } = useAuth()
  const authed = status === 'authenticated'
  const { showToast } = useToast()
  const queryClient = useQueryClient()

  const [open, setOpen] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  // null | { address: null } (add) | { address } (edit)
  const [modal, setModal] = useState(null)
  // Address awaiting the styled remove confirmation, or null.
  const [confirmRemove, setConfirmRemove] = useState(null)
  const [removing, setRemoving] = useState(false)

  const { data, isPending } = useQuery({
    queryKey: ['addresses'],
    queryFn: () => api.addresses.list(),
    enabled: authed,
    retry: false,
  })
  // Upstream reorders the list (default-first) once a selection flips
  // `is_default`, which would otherwise make the cards jump around on
  // every refetch. Keep a stable display order regardless of default status.
  const addresses = toList(data).slice().sort((a, b) => (a.id ?? 0) - (b.id ?? 0))

  // Default (or first) address counts as selected until the user picks one.
  const effectiveSelectedId =
    selectedId ?? addresses.find(isDefault)?.id ?? addresses[0]?.id ?? null

  // Surface the effective selection to the parent — checkout needs the
  // address id even when the user never clicks a card (default selection).
  const selectedAddress = addresses.find((a) => a.id === effectiveSelectedId) ?? null
  useEffect(() => {
    onSelect?.(selectedAddress)
  }, [selectedAddress?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['addresses'] })

  const deliverHere = async (a) => {
    setSelectedId(a.id)
    onSelect?.(a)
    if (isDefault(a)) return
    try {
      await api.addresses.setDefault(a.id)
      invalidate()
      showToast(`Delivering to ${displayName(a)}, ${a.city || a.shipping_city?.name || ''}`.trim())
    } catch {
      showToast('Could not set this as the default address — try again.')
    }
  }

  const removeAddress = async () => {
    const a = confirmRemove
    if (!a) return
    setRemoving(true)
    try {
      await api.addresses.remove(a.id)
      showToast('Address removed')
      setConfirmRemove(null)
      invalidate()
    } catch {
      showToast('Could not remove the address — try again.')
    } finally {
      setRemoving(false)
    }
  }

  return (
    <div className="bg-white rounded-3xl border border-border/40 overflow-hidden shadow-sm">
      {/* Accordion header */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full p-6 flex items-center gap-4 bg-muted/10 hover:bg-muted/20 transition-colors text-left"
      >
        <svg className="w-6 h-6 text-secondary shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path>
          <circle cx="12" cy="10" r="3"></circle>
        </svg>
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-heading text-primary font-normal uppercase">DELIVERY COORDINATES</h3>
          <p className="text-[10px] font-mono font-normal text-primary/30 uppercase tracking-widest">
            {addresses.length > 0 ? `${addresses.length} SAVED ADDRESS${addresses.length > 1 ? 'ES' : ''}` : 'DISPATCH IDENTITY POINT'}
          </p>
          {/* Collapsed: the selected address's estimate (open: each card shows its own) */}
          {!open && selectedAddress && (
            <div className="mt-1.5 normal-case">
              <DeliveryEstimate pincode={selectedAddress.shipping_zip_code || selectedAddress.pincode} />
            </div>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-primary/40 shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {/* Accordion body */}
      {open && (
        <div className="border-t border-border p-6 md:p-8 space-y-4">
          {!authed ? (
            <p className="text-xs text-primary/50 font-body normal-case text-center py-6">
              Sign in to manage your saved delivery addresses.
            </p>
          ) : isPending ? (
            <div className="space-y-4">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-border p-6 animate-pulse space-y-3">
                  <div className="h-4 w-48 rounded bg-gray-200" />
                  <div className="h-3 w-72 rounded bg-gray-100" />
                  <div className="h-3 w-40 rounded bg-gray-100" />
                </div>
              ))}
            </div>
          ) : (
            <>
              {addresses.length === 0 && (
                <p className="text-xs text-primary/50 font-body normal-case text-center py-6">
                  No saved addresses yet — add one below.
                </p>
              )}

              {addresses.map((a) => {
                const selected = effectiveSelectedId === a.id
                const type = String(a.address_type || a.type || '').toUpperCase()
                return (
                  <div
                    key={a.id}
                    className={`rounded-2xl border py-2 px-4 transition-colors cursor-pointer ${selected ? 'border-secondary bg-secondary/3' : 'border-border bg-white'}`}
                    onClick={() => deliverHere(a)}
                  >
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        type="button"
                        aria-label={`Deliver to ${displayName(a)}`}
                        aria-pressed={selected}
                        className={`h-4 w-4 rounded-full border-2 grid place-items-center shrink-0 ${selected ? 'border-secondary' : 'border-border'}`}
                      >
                        {selected && <span className="h-1.5 w-1.5 rounded-full bg-secondary" />}
                      </button>
                      <span className="text-sm font-semibold text-primary uppercase tracking-wide">{displayName(a)}</span>
                      {type && (
                        <span className="px-2 py-0.5 rounded border border-border text-[8px] font-bold tracking-widest text-primary/50 uppercase">{type}</span>
                      )}
                      {isDefault(a) && (
                        <span className="px-2 py-0.5 rounded bg-secondary text-white text-[8px] font-bold tracking-widest uppercase">DEFAULT</span>
                      )}
                    </div>

                    <div className="mt-3 pl-7 space-y-0.5 text-xs text-primary/70 font-body normal-case leading-relaxed">
                      <p>{a.billing_address}</p>
                      {a.landmark && <p>Landmark: {a.landmark}</p>}
                      <p>{[a?.shipping_city?.name, a?.shipping_state?.name, a?.shipping_zip_code].filter(Boolean).join(', ')}</p>
                      {a?.shipping_country?.name && <p>{a.shipping_country.name}</p>}
                      {a?.shipping_phone_number && <p className="text-primary/40">Mobile: {a.shipping_phone_number}</p>}
                    </div>

                    <div className="mt-2 pl-7">
                      <DeliveryEstimate pincode={a.shipping_zip_code || a.pincode} />
                    </div>

                    <div className="mt-4 pl-7 flex items-center gap-4 flex-wrap">
                      {selected && (
                        <div
                          className="inline-flex items-center justify-center px-5 py-2 rounded-md bg-secondary text-white uppercase font-semibold text-[9px] tracking-widest"
                        >
                          DELIVER HERE
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setModal({ address: a }) }}
                        className="text-[10px] font-semibold uppercase tracking-widest text-secondary hover:text-primary transition-colors"
                      >
                        EDIT
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setConfirmRemove(a) }}
                        className="text-[10px] font-semibold uppercase tracking-widest text-danger/60 hover:text-danger transition-colors"
                      >
                        REMOVE
                      </button>
                    </div>
                  </div>
                )
              })}

              <button
                type="button"
                onClick={() => setModal({ address: null })}
                className="w-full h-12 rounded-2xl border border-dashed border-border text-primary/50 hover:border-secondary hover:text-primary transition-all uppercase tracking-widest text-[10px] font-semibold"
              >
                + ADD NEW ADDRESS
              </button>
            </>
          )}
        </div>
      )}

      {modal && (
        <AddressModal
          address={modal.address}
          user={user}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null)
            invalidate()
          }}
        />
      )}

      {confirmRemove && (
        <ConfirmDialog
          title="REMOVE ADDRESS"
          message={
            <>
              Remove the address <strong className="text-primary">“{displayName(confirmRemove)}”</strong>
              {confirmRemove.shipping_city?.name ? `, ${confirmRemove.shipping_city.name}` : ''}? This can't be undone.
            </>
          }
          confirmLabel="REMOVE"
          busy={removing}
          onConfirm={removeAddress}
          onCancel={() => !removing && setConfirmRemove(null)}
        />
      )}
    </div>
  )
}

/** Styled replacement for window.confirm — same overlay treatment as the address modal. */
function ConfirmDialog({ title, message, confirmLabel, busy, onConfirm, onCancel }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-3xl border border-border/40 shadow-2xl p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-2xl bg-danger/10 grid place-items-center shrink-0">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-danger">
              <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
            </svg>
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-heading text-primary font-normal uppercase">{title}</h3>
            <p className="mt-2 text-xs text-primary/60 font-body normal-case leading-relaxed">{message}</p>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="text-[10px] font-semibold uppercase tracking-widest text-primary/50 hover:text-primary transition-colors disabled:opacity-50"
          >
            CANCEL
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center justify-center h-10 px-6 rounded-xl bg-danger text-white hover:bg-primary transition-all active:scale-95 disabled:opacity-50 disabled:pointer-events-none uppercase font-semibold text-[10px] tracking-widest"
          >
            {busy ? 'REMOVING…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

const normName = (s) => String(s || '').trim().toLowerCase()
const toOption = (x) => ({ value: x.id, label: x.name })

/** Add/edit popup — field set mirrors the storefront reference form. */
function AddressModal({ address, user, onClose, onSaved }) {
  const { showToast } = useToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(() => {
    if (address) return toForm(address)
    // New address: seed the name from the session user.
    const parts = String(user?.name || '').trim().split(/\s+/).filter(Boolean)
    return { ...EMPTY_FORM, firstName: parts[0] || '', lastName: parts.slice(1).join(' ') }
  })
  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  // Indian states (POST /get-state-list), then cities for the picked state
  // (POST /get-city-list { state_id }). Reference data — cache for the session.
  const statesQuery = useQuery({
    queryKey: ['states'],
    queryFn: () => api.lookups.states(),
    staleTime: Infinity,
    retry: false,
  })
  const states = toList(statesQuery.data)
  // Editing an old record may only carry the state name — fall back to a name match.
  const selectedState =
    states.find((s) => s.id === form.stateId) ??
    states.find((s) => normName(s.name) === normName(form.state)) ??
    null

  const citiesQuery = useQuery({
    queryKey: ['cities', selectedState?.id],
    queryFn: () => api.lookups.cities(selectedState.id),
    enabled: selectedState != null,
    staleTime: Infinity,
    retry: false,
  })
  const cities = toList(citiesQuery.data)
  const selectedCity =
    cities.find((c) => c.id === form.cityId) ??
    cities.find((c) => normName(c.name) === normName(form.city)) ??
    null

  const onStateChange = (opt) =>
    setForm((f) => ({ ...f, state: opt?.label || '', stateId: opt?.value ?? null, city: '', cityId: null }))
  const onCityChange = (opt) =>
    setForm((f) => ({ ...f, city: opt?.label || '', cityId: opt?.value ?? null }))

  const submit = async () => {
    if (!form.firstName.trim()) return showToast('Enter the first name')
    if (!/^\d{10}$/.test(form.mobile.trim())) return showToast('Enter a valid 10-digit mobile number')
    if (!/^\d{6}$/.test(form.pincode.trim())) return showToast('Enter a valid 6-digit pincode')
    if (!form.address.trim()) return showToast('Enter the address')
    if (!selectedState) return showToast('Select the state')
    if (!selectedCity) return showToast('Select the city')

    // /address-save body: state/city/country are lookup ids (as strings), phone not mobile.
    const payload = {
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim(),
      email: user?.email || '',
      phone: form.mobile.trim(),
      address: form.address.trim(),
      landmark: form.landmark.trim(),
      city: String(selectedCity.id),
      state: String(selectedState.id),
      country: '1',
      pincode: form.pincode.trim(),
      type: form.type,
    }

    setSaving(true)
    try {
      if (address) await api.addresses.update({ address_id: address.id, ...payload })
      else await api.addresses.save(payload)
      showToast(address ? 'Address updated' : 'Address saved')
      onSaved()
    } catch {
      showToast('Could not save the address — try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-3xl border border-border/40 shadow-2xl p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={address ? 'Edit address' : 'Add address'}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-heading text-primary font-normal uppercase">
            {address ? 'EDIT ADDRESS' : 'ADD NEW ADDRESS'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-2 rounded-xl text-primary/40 hover:text-primary hover:bg-muted/30 transition-all"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ModalField label="FIRST NAME" placeholder="First name" value={form.firstName} onChange={setField('firstName')} />
            <ModalField label="LAST NAME" placeholder="Last name" value={form.lastName} onChange={setField('lastName')} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ModalField label="MOBILE NUMBER" placeholder="10-digit mobile number" inputMode="numeric" value={form.mobile} onChange={setField('mobile')} />
            <ModalField label="PINCODE" placeholder="Pincode" inputMode="numeric" value={form.pincode} onChange={setField('pincode')} />
          </div>
          <ModalField label="ADDRESS (HOUSE NO, BUILDING, AREA)" placeholder="Flat / House no., Building, Street, Area" value={form.address} onChange={setField('address')} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField
              label="STATE"
              placeholder="Select state"
              options={states.map(toOption)}
              value={selectedState ? toOption(selectedState) : null}
              onChange={onStateChange}
              isLoading={statesQuery.isLoading}
              isClearable
            />
            <SelectField
              label="CITY / DISTRICT"
              placeholder={selectedState ? 'Select city' : 'Pick a state first'}
              options={cities.map(toOption)}
              value={selectedCity ? toOption(selectedCity) : null}
              onChange={onCityChange}
              isLoading={citiesQuery.isLoading}
              isDisabled={!selectedState}
              isClearable
            />
          </div>
          <ModalField label="LANDMARK (OPTIONAL)" placeholder="Nearby landmark" value={form.landmark} onChange={setField('landmark')} />

          <div>
            <p className="text-[10px] font-semibold text-primary/50 uppercase tracking-widest mb-2">ADDRESS TYPE</p>
            <div className="flex items-center gap-6">
              {['home', 'office', 'other'].map((t) => (
                <label key={t} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="address-type"
                    value={t}
                    checked={form.type === t}
                    onChange={setField('type')}
                    className="accent-[#E8770F] h-4 w-4"
                  />
                  <span className="text-xs text-primary/70 uppercase tracking-wide">{t}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4 pt-4">
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="inline-flex items-center justify-center h-12 px-8 rounded-xl bg-secondary text-white hover:bg-primary transition-all active:scale-95 disabled:opacity-50 disabled:pointer-events-none uppercase font-semibold text-[10px] tracking-widest"
            >
              {saving ? 'SAVING…' : address ? 'UPDATE ADDRESS' : 'SAVE AND DELIVER HERE'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-[10px] font-semibold uppercase tracking-widest text-secondary hover:text-primary transition-colors"
            >
              CANCEL
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// react-select styling matched to ModalField (h-12, rounded-xl, theme borders).
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '3rem',
    borderRadius: '0.75rem',
    borderColor: state.isFocused ? 'var(--color-secondary)' : 'var(--color-border)',
    boxShadow: 'none',
    fontSize: '0.875rem',
    '&:hover': { borderColor: state.isFocused ? 'var(--color-secondary)' : 'var(--color-border)' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 1rem' }),
  placeholder: (base) => ({ ...base, color: 'color-mix(in srgb, var(--color-primary) 30%, transparent)' }),
  menu: (base) => ({ ...base, borderRadius: '0.75rem', overflow: 'hidden' }),
  // The menu portals to <body> so the modal's overflow-y-auto can't clip it —
  // keep it above the modal overlay (z-50).
  menuPortal: (base) => ({ ...base, zIndex: 60 }),
  option: (base, state) => ({
    ...base,
    fontSize: '0.875rem',
    backgroundColor: state.isSelected
      ? 'var(--color-secondary)'
      : state.isFocused
        ? 'color-mix(in srgb, var(--color-secondary) 10%, white)'
        : 'white',
  }),
}

function SelectField({ label, ...props }) {
  return (
    <div className="normal-case">
      <label className="block text-[10px] font-semibold text-primary/50 uppercase tracking-widest mb-2">{label}</label>
      <Select styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" {...props} />
    </div>
  )
}

function ModalField({ label, className = '', ...props }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold text-primary/50 uppercase tracking-widest mb-2">{label}</label>
      <input
        className={`w-full h-12 px-4 rounded-xl border border-border bg-white text-sm text-primary font-normal normal-case placeholder:text-primary/30 focus:outline-none focus:border-secondary transition-colors ${className}`}
        {...props}
      />
    </div>
  )
}
